/**
 * UCDP panel — Uppsala armed conflict events.
 *
 * Filters events where source === 'ucdp'. Type tabs for
 * All, State-based, Non-state, One-sided. Click → fly to location.
 */

import { appStore } from '@/core/store';
import { relativeTime } from '@/utils/time';
import { getMap } from '@/map/engine';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

type ConflictType = 'all' | 'state-based' | 'non-state' | 'one-sided';

const TYPE_TABS: { label: string; value: ConflictType }[] = [
  { label: 'All', value: 'all' },
  { label: 'State-based', value: 'state-based' },
  { label: 'Non-state', value: 'non-state' },
  { label: 'One-sided', value: 'one-sided' },
];

export function createUcdpPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'ucdp-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const tabBar = document.createElement('div');
  tabBar.style.cssText = `
    display: flex; gap: 4px; padding: 8px 12px; flex-wrap: wrap;
    border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  `;
  container.appendChild(tabBar);

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px; flex: 1; overflow-y: auto;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let activeTab: ConflictType = 'all';

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function renderTabs(): void {
    tabBar.textContent = '';
    for (const tab of TYPE_TABS) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding: 4px 10px; border-radius: 12px; border: 1px solid var(--border-primary);
        font-size: 11px; cursor: pointer; transition: all 150ms;
        background: ${activeTab === tab.value ? 'var(--accent)' : 'var(--bg-secondary)'};
        color: ${activeTab === tab.value ? '#fff' : 'var(--text-secondary)'};
      `;
      btn.textContent = tab.label;
      btn.onclick = () => {
        activeTab = tab.value;
        renderTabs();
        scheduleRender();
      };
      tabBar.appendChild(btn);
    }
  }

  function inferConflictType(ev: NormalizedEvent): ConflictType {
    const tags = ev.tags ?? [];
    for (const tag of tags) {
      if (tag === 'state-based' || tag === 'non-state' || tag === 'one-sided') return tag;
    }
    const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();
    if (text.includes('one-sided') || text.includes('one sided') || text.includes('civilian')) return 'one-sided';
    if (text.includes('non-state') || text.includes('non state') || text.includes('militia') || text.includes('rebel vs')) return 'non-state';
    if (text.includes('government') || text.includes('state') || text.includes('military')) return 'state-based';
    return 'state-based';
  }

  function render(): void {
    const events = appStore.getState().events;
    let ucdpEvents = events
      .filter(e => e.source === 'ucdp')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (activeTab !== 'all') {
      ucdpEvents = ucdpEvents.filter(e => inferConflictType(e) === activeTab);
    }

    contentArea.textContent = '';

    // Count header
    const countHeader = document.createElement('div');
    countHeader.style.cssText = `
      display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${ucdpEvents.length > 0 ? 'var(--severity-critical)' : 'var(--text-muted)'};`;
    countNum.textContent = String(ucdpEvents.length);
    countHeader.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `active conflict${ucdpEvents.length !== 1 ? 's' : ''}`;
    countHeader.appendChild(countLabel);

    contentArea.appendChild(countHeader);

    if (ucdpEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No UCDP conflict events';
      contentArea.appendChild(msg);
      return;
    }

    for (const ev of ucdpEvents.slice(0, 80)) {
      const sev = ev.severity ?? 'medium';
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary); border-left: 3px solid ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
        cursor: pointer;
      `;

      // Click → fly to location
      if (ev.location) {
        row.onclick = () => {
          const map = getMap();
          if (map && ev.location) {
            map.flyTo({
              center: [ev.location.lng, ev.location.lat],
              zoom: Math.max(map.getZoom(), 6),
              duration: 600,
            });
          }
        };
        row.title = 'Click to fly to location';
      }

      // Title row: conflict name
      const titleEl = document.createElement('div');
      titleEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary); margin-bottom: 3px;';
      titleEl.textContent = ev.title;
      row.appendChild(titleEl);

      // Details row: parties, location, severity
      const detailRow = document.createElement('div');
      detailRow.style.cssText = 'display: flex; align-items: center; gap: 6px; flex-wrap: wrap;';

      // Conflict type badge
      const typeBadge = document.createElement('span');
      typeBadge.style.cssText = `
        font-size: 9px; padding: 1px 5px; border-radius: 3px;
        background: var(--bg-primary); color: var(--text-muted);
      `;
      typeBadge.textContent = inferConflictType(ev).replace('-', ' ');
      detailRow.appendChild(typeBadge);

      // Location
      if (ev.location?.name || ev.location?.country) {
        const locEl = document.createElement('span');
        locEl.style.cssText = 'font-size: 10px; color: var(--text-secondary);';
        locEl.textContent = ev.location.name ?? ev.location.country ?? '';
        detailRow.appendChild(locEl);
      }

      // Severity / fatality indicator
      const sevBadge = document.createElement('span');
      sevBadge.style.cssText = `
        font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 3px;
        text-transform: uppercase; margin-left: auto;
        background: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'}22;
        color: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
      `;
      sevBadge.textContent = sev;
      detailRow.appendChild(sevBadge);

      row.appendChild(detailRow);

      // Timestamp
      const time = document.createElement('div');
      time.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 3px;';
      time.textContent = relativeTime(ev.timestamp);
      row.appendChild(time);

      contentArea.appendChild(row);
    }
  }

  renderTabs();
  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
